import {
  type BrowserEditorTabCatalog,
  createMemoryBrowserEditorTabCatalog,
  parseBrowserEditorTabSlots,
  serializeBrowserEditorTabSlots,
} from '@hibro/shared';

/** Same catalog the desktop host writes to `hibro-browser-tabs.json` (见 22). */
export const WEB_BROWSER_EDITOR_TABS_KEY = 'hibro-browser-editor-tabs';

/**
 * Workbench-page storage for restored editor tabs. A missing key is an empty
 * catalog. Unreadable JSON throws — a corrupt catalog must not look like
 * "the user had no tabs".
 */
export function createLocalStorageBrowserEditorTabCatalog(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  onPersistError: (error: unknown) => void,
): BrowserEditorTabCatalog {
  const raw = storage.getItem(WEB_BROWSER_EDITOR_TABS_KEY);
  const memory = createMemoryBrowserEditorTabCatalog(
    raw === null ? {} : parseBrowserEditorTabSlots(JSON.parse(raw)),
  );
  const persist = (): void => {
    try {
      storage.setItem(
        WEB_BROWSER_EDITOR_TABS_KEY,
        serializeBrowserEditorTabSlots(memory.entries()),
      );
    } catch (error) {
      onPersistError(error);
    }
  };
  return {
    read: (slotId) => memory.read(slotId),
    readTitle: (slotId) => memory.readTitle(slotId),
    remember(slotId, url, title) {
      const previousUrl = memory.read(slotId);
      const previousTitle = memory.readTitle(slotId);
      memory.remember(slotId, url, title);
      if (
        memory.read(slotId) !== previousUrl ||
        memory.readTitle(slotId) !== previousTitle
      ) {
        persist();
      }
    },
    forget(slotId) {
      if (memory.read(slotId) === undefined) {
        return;
      }
      memory.forget(slotId);
      persist();
    },
  };
}
